import { Children, Fragment, forwardRef, isValidElement, useRef, type CSSProperties, type FocusEvent, type ReactElement, type ReactNode, type Ref } from "react";
import MuiSelect, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";

// Thin wrapper mapping antd <Select> onto MUI Select+MenuItem so the ~27 call
// sites swap by import only and keep feature parity. Generic over the value
// type so typed handlers (e.g. (v: number) => void) stay type-safe. Supports
// both the options prop and Select.Option children. When `showSearch` is set,
// renders a MUI Autocomplete instead (antd searchable-Select parity: onSearch,
// filterOption, notFoundContent). Other antd-only props are accepted for compat.

// antd <Select.Option> maps directly to MUI <MenuItem>: value/children/disabled
// are compatible, and MUI Select introspects real MenuItem children for value
// (a wrapper component would break that introspection).
export const Option = MenuItem;

export interface SelectOption {
  value: string | number;
  label: ReactNode;
  disabled?: boolean;
}

// Derive an option list from the `options` prop, else from antd
// <Option value>label</Option> children. Shared by the searchable (Autocomplete)
// branch and the plain-Select placeholder renderValue.
function toOptionList(options: SelectOption[] | undefined, children: ReactNode): SelectOption[] {
  if (options) return options;
  if (children == null) return [];
  return Children.toArray(children)
    .filter(isValidElement)
    .map((c) => {
      const el = c as ReactElement<{ value?: string | number; children?: ReactNode }>;
      return { value: el.props.value as string | number, label: el.props.children };
    });
}

// MUI's Autocomplete decides whether to overwrite the input text by comparing
// `value` BY REFERENCE (useAutocomplete: `value !== previousProps.value` ->
// resetInputValue). The searchable branch resolves `value` to an option object
// rebuilt on every render, so once anything was selected every keystroke's
// re-render forced the selected label — or, under server-side search where the
// selection is not among the results, the raw value — back into the input and the
// user could never finish typing (#5176). Same displayed option(s) => same object,
// so MUI only resets when the selection really changed (a label that resolves
// later, e.g. options loading async, still refreshes the input).
function sameAcValue(
  a: SelectOption | SelectOption[] | null,
  b: SelectOption | SelectOption[] | null,
  labelOf: (o: unknown) => string,
): boolean {
  const sameOpt = (x: SelectOption, y: SelectOption) => x.value === y.value && labelOf(x) === labelOf(y);
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((o, i) => sameOpt(o, b[i]));
  if (a && b && !Array.isArray(a) && !Array.isArray(b)) return sameOpt(a, b);
  return a === b;
}

// antd fires onSelect/onDeselect once per item in multiple mode, while MUI
// reports the whole new array — derive the per-item events by diffing. Both the
// plain and the searchable branch need this: a call site that only listens to
// onSelect/onDeselect otherwise never hears a pick (#5334).
function fireItemEvents(
  prevValue: unknown,
  next: Array<string | number>,
  onSelect?: (value: string | number) => void,
  onDeselect?: (value: string | number) => void,
) {
  const prev = (Array.isArray(prevValue) ? prevValue : []) as Array<string | number>;
  next.filter((v) => !prev.includes(v)).forEach((v) => onSelect?.(v));
  prev.filter((v) => !next.includes(v)).forEach((v) => onDeselect?.(v));
}

export interface SelectProps<T = string | number | Array<string | number>> {
  value?: T | null;
  defaultValue?: T | null;
  onChange?: (value: T, option?: unknown) => void;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  name?: string;
  status?: "error" | "warning" | "" | false;
  dropdownStyle?: CSSProperties;
  // In multiple mode antd fires these with a single item (not the array), so
  // the param is intentionally loose rather than the whole-value type T.
  // Method-signature form so call sites with narrower option types are accepted.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onSelect?(value: any, option?: any): void;
  onDeselect?(value: any, option?: any): void;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  onSearch?: (text: string) => void;
  onDropdownVisibleChange?: (open: boolean) => void;
  mode?: "multiple" | "tags";
  options?: SelectOption[];
  placeholder?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  showSearch?: boolean;
  filterOption?: boolean | ((input: string, option?: SelectOption) => boolean);
  notFoundContent?: ReactNode;
  dropdownRender?: (menu: ReactNode) => ReactNode;
  optionRender?: (option: any) => ReactNode; // eslint-disable-line @typescript-eslint/no-explicit-any -- compat shim: call sites shape their own option
  getPopupContainer?: unknown;
  showArrow?: boolean;
  maxTagCount?: number;
  popupClassName?: string;
  popupMatchSelectWidth?: boolean | number;
  optionLabelProp?: string;
  placement?: string;
  open?: boolean;
  onClear?: () => void;
  size?: "small" | "middle" | "large";
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
  id?: string;
  "data-cy"?: string;
  "data-testid"?: string;
  "aria-label"?: string;
}

function SelectComponent<T = string | number | Array<string | number>>({
  value,
  defaultValue,
  onChange,
  onSelect,
  onDeselect,
  onBlur,
  name,
  status,
  mode,
  options,
  disabled,
  size = "middle",
  style,
  className,
  children,
  // used by the searchable (showSearch) Autocomplete branch:
  placeholder,
  loading,
  allowClear = true,
  showSearch,
  filterOption,
  notFoundContent,
  onSearch,
  optionRender,
  // accepted for antd compat, no MUI equivalent (ignored):
  /* eslint-disable @typescript-eslint/no-unused-vars */
  dropdownRender,
  getPopupContainer,
  showArrow,
  maxTagCount,
  popupClassName,
  onClear,
  onDropdownVisibleChange,
  optionLabelProp,
  popupMatchSelectWidth,
  placement,
  dropdownStyle,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  "data-cy": dataCy,
  "data-testid": dataTestid,
  ...rest
}: SelectProps<T>, ref: Ref<unknown>) {
  const theme = useTheme();
  const multiple = mode === "multiple" || mode === "tags";
  // Reference-stable Autocomplete value; see sameAcValue.
  const acValueCache = useRef<SelectOption | SelectOption[] | null>(null);
  // antd Selects fill their form/filter container; MUI FormControl is content-width.
  // Default fullWidth. Honor an explicit style.width (drop the minWidth floor so a
  // width smaller than 120 still wins); keep minWidth:120 only when no width is given.
  const hasWidth = style?.width != null;

  // antd showSearch (or an onSearch handler) => a type-to-filter combobox. MUI
  // Select can't search, so render an Autocomplete instead (keeps onSearch/
  // filterOption/notFoundContent/optionRender parity).
  if (showSearch || onSearch) {
    // Prefer the options prop; otherwise derive from antd <Option value>label</Option>
    // children so the searchable branch works for the children API too.
    const opts = toOptionList(options, children);
    const labelOf = (o: unknown): string => {
      if (typeof o === "string") return o;
      const opt = o as SelectOption | null;
      if (typeof opt?.label === "string") return opt.label;
      return String(opt?.value ?? "");
    };
    // A selected value missing from the current options (a server-side search
    // moved on) keeps the label it was shown with, like antd, instead of its raw value.
    const shownOpts = ([] as SelectOption[]).concat(acValueCache.current ?? []);
    const findOpt = (v: unknown): SelectOption | null =>
      opts.find((o) => o.value === v) ??
      shownOpts.find((o) => o.value === v) ??
      (v != null && v !== "" ? { value: v as string | number, label: String(v) } : null);
    const nextAcValue = multiple
      ? (Array.isArray(value) ? value : []).map(findOpt).filter((o): o is SelectOption => o !== null)
      : findOpt(value ?? undefined);
    if (!sameAcValue(acValueCache.current, nextAcValue, labelOf)) acValueCache.current = nextAcValue;
    const acValue = acValueCache.current;
    // antd filterOption(input, option) -> MUI filterOptions(options, state). false = server-filtered (passthrough).
    const filterOptions =
      filterOption === false
        ? (x: SelectOption[]) => x
        : typeof filterOption === "function"
          ? (list: SelectOption[], state: { inputValue: string }) => list.filter((o) => filterOption(state.inputValue, o))
          : undefined; // MUI default (substring on label)
    return (
      <MuiAutocomplete<SelectOption, boolean, boolean, false>
        data-cy={dataCy}
        data-testid={dataTestid}
        multiple={multiple}
        options={opts}
        value={(acValue ?? (multiple ? [] : null)) as never}
        getOptionLabel={labelOf}
        isOptionEqualToValue={(o, v) => o?.value === v?.value}
        disabled={disabled}
        disableClearable={!allowClear}
        loading={loading}
        noOptionsText={notFoundContent ?? "No results"}
        {...(filterOptions ? { filterOptions: filterOptions as never } : {})}
        renderOption={(liProps, option) => {
          // MUI forces options to min-height 48px + wide padding, oversizing rich
          // options. Match the AutoComplete wrapper: size to content, tighter padding.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { style: liStyle, ...restLi } = liProps as any;
          return (
            <li
              {...restLi}
              style={{ minHeight: 0, paddingTop: 4, paddingBottom: 4, ...(liStyle || {}) }}
              key={String(option.value)}
            >
              {optionRender ? optionRender({ ...option, data: option }) : (option.label ?? labelOf(option))}
            </li>
          );
        }}
        onInputChange={(_e, val, reason) => {
          if (reason === "input") onSearch?.(val);
        }}
        onChange={(_e, picked) => {
          if (multiple) {
            const next = (picked as SelectOption[]).map((o) => o.value);
            onChange?.(next as T);
            fireItemEvents(value, next, onSelect, onDeselect);
          } else {
            const opt = picked as SelectOption | null;
            onChange?.((opt?.value ?? undefined) as T, opt ?? undefined);
            onSelect?.(opt?.value, opt ?? undefined);
          }
        }}
        fullWidth={!hasWidth}
        style={{ ...(hasWidth ? {} : { minWidth: 120 }), ...style }}
        className={className}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={ref}
            name={name}
            error={status === "error"}
            onBlur={onBlur}
            placeholder={typeof placeholder === "string" ? placeholder : undefined}
            size={size === "large" ? "medium" : "small"}
          />
        )}
      />
    );
  }

  const handleChange = (e: SelectChangeEvent<unknown>) => {
    const next = e.target.value as T;
    onChange?.(next);
    if (multiple) {
      fireItemEvents(value, (Array.isArray(next) ? next : []) as Array<string | number>, onSelect, onDeselect);
    } else {
      onSelect?.(next);
    }
  };

  // renderValue restores two antd behaviors MUI's default rendering drops:
  //   1. placeholder — antd shows muted placeholder text while nothing is selected
  //      (MUI has no floating label here, so a placeholder-only Select is blank);
  //   2. echo — antd displays a `value` that matches no option as raw text, while
  //      MUI blanks an out-of-range value. Notes Category/Access relied on this
  //      (they bind `value` to the label string, options carry numeric ids), so
  //      gating renderValue behind a placeholder blanked them. Always attach it.
  // For a value that DOES match an option it renders that option's label, so
  // matched Selects are unchanged.
  const plainOpts = toOptionList(options, children);
  const labelForValue = (v: string | number): ReactNode => {
    const opt = plainOpts.find((o) => o.value === v);
    return opt ? opt.label : String(v);
  };
  const isEmptyValue = (v: unknown) =>
    multiple ? !Array.isArray(v) || v.length === 0 : v == null || v === "";
  const renderValue = (selected: unknown): ReactNode => {
    if (isEmptyValue(selected)) {
      // placeholder may be undefined (no placeholder set) -> empty muted span, i.e. blank.
      return <span style={{ color: theme.palette.text.secondary }}>{placeholder}</span>;
    }
    if (multiple && Array.isArray(selected)) {
      return selected.map((v, i) => (
        <Fragment key={String(v)}>
          {i > 0 ? ", " : ""}
          {labelForValue(v as string | number)}
        </Fragment>
      ));
    }
    return labelForValue(selected as string | number);
  };

  return (
    <MuiSelect
      inputRef={ref}
      name={name}
      error={status === "error"}
      onBlur={onBlur}
      displayEmpty
      renderValue={renderValue}
      // Controlled unless the call site passes ONLY defaultValue. A value that
      // starts undefined (async load) must still mount controlled (value="") so
      // it adopts the id when it resolves; a defaultValue-only select stays
      // uncontrolled so it isn't blanked by a coerced value="".
      // `value` wins when both are given (React's own precedence): the RHF
      // template inputs mirror field.value into both, so preferring defaultValue
      // flipped them controlled -> uncontrolled on the first pick and stranded
      // the display on the mounted "" (#4889).
      {...(defaultValue !== undefined && value === undefined
        ? { defaultValue: defaultValue as unknown }
        : { value: (value ?? (multiple ? [] : "")) as unknown })}
      multiple={multiple}
      disabled={disabled}
      size={size === "large" ? "medium" : "small"}
      onChange={handleChange}
      fullWidth={!hasWidth}
      style={{ ...(hasWidth ? {} : { minWidth: 120 }), ...style }}
      className={className}
      data-cy={dataCy}
      data-testid={dataTestid}
      {...rest}
    >
      {children ??
        options?.map((o) => (
          <MenuItem key={String(o.value)} value={o.value} disabled={o.disabled}>
            {o.label}
          </MenuItem>
        ))}
    </MuiSelect>
  );
}

// forwardRef so RHF `field.ref` / explicit ref reach the input; the cast
// preserves the generic call signature + attaches the Option static.
export const Select = forwardRef(SelectComponent) as unknown as (<
  T = string | number | Array<string | number>,
>(
  props: SelectProps<T> & { ref?: Ref<unknown> }
) => ReactElement) & { Option: typeof Option };
Select.Option = Option;

export default Select;
